import { ChangeEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CustomButton, CustomCheckBox } from '../../../shared/uiComponents';
import CustomDrawer from '../../../shared/uiComponents/Drawer';
import { AdminTypes } from '../../../components/Menu';
import AdminTools from './adminTools';

import { SoapNotesStatuses } from '../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { DispatchProperties, useSelector } from '../../../redux/store';
import { clearEvents } from '../../../redux/State/clientSlice/calendarSlice';
import {
	setApplyFilter,
	setClient,
	setIncludeBCBAEvents,
	setIncludeBTEvents,
	setOpenFilters,
	setSearchClient,
	setSearchTherapist,
	setShowCompleted,
	setShowDraftsOnly,
	setTherapist,
} from '../../../redux/State/clientSlice/calendarHelperSlice';

const CalendarFilters = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const userId = useSelector((state) => state.account.user.id);
	const role = useSelector((state) => state.account.role);
	const events = useSelector((state) => state.calendar.events);
	const includeBTEvents = useSelector((state) => state.calendarHelper.filterData.includeBTEvents);
	const includeBCBAEvents = useSelector((state) => state.calendarHelper.filterData.includeBCBAEvents);
	const showDraftsOnly = useSelector((state) => state.calendarHelper.filterData.showDraftsOnly);
	const showCompleted = useSelector((state) => state.calendarHelper.filterData.showCompleted);

	const { client, therapist } = useSelector((state) => state.calendarHelper.filterData);

	const userSubmittedSessions = events?.length
		? events?.filter(
				(x) =>
					x.isCompleted &&
					x.reportStatus &&
					x.reportStatus.status === SoapNotesStatuses.submitted &&
					x.user.id === userId,
			).length
		: 0;

	const userBTSubmittedSessions = events?.length
		? events?.filter(
				(x) =>
					x.isCompleted &&
					x.reportStatus &&
					x.reportStatus.status === SoapNotesStatuses.submitted &&
					x.user.id !== userId &&
					x.user.role.section.id === AdminTypes.bt,
			).length
		: 0;

	const userBCBASubmittedSessions = events?.length
		? events?.filter(
				(x) =>
					x.isCompleted &&
					x.reportStatus &&
					x.reportStatus.status === SoapNotesStatuses.submitted &&
					x.user.id !== userId &&
					x.user.role.section.id === AdminTypes.bcba,
			).length
		: 0;

	const userBTSessions = events?.length
		? events?.filter((x) => x.user.id !== userId && x.user.role.section.id === AdminTypes.bt).length
		: 0;

	const userBCBASessions = events?.length
		? events?.filter((x) => x.user.id !== userId && x.user.role.section.id === AdminTypes.bcba).length
		: 0;

	const drafts = events?.length
		? events?.filter((x) => x.reportStatus && x.reportStatus.status === SoapNotesStatuses.draft).length
		: 0;

	const completedSessions = userSubmittedSessions
		? includeBCBAEvents && includeBTEvents
			? `${userSubmittedSessions} - ${userBTSubmittedSessions} - ${userBCBASubmittedSessions}`
			: includeBTEvents
				? `${userSubmittedSessions} - ${userBTSubmittedSessions}`
				: includeBCBAEvents
					? `${userSubmittedSessions} - ${userBCBASubmittedSessions}`
					: userSubmittedSessions
		: 0;

	const labelCompleted = `Show Completed Sessions (${completedSessions})`;

	const labelBT = `Show BT Sessions ${userBTSessions ? `(${userBTSessions})` : ''}`;

	const labelBCBA = `Show BCBA Sessions ${userBCBASessions ? `(${userBCBASessions})` : ''}`;

	const labelDrafts = `Show Drafts ${drafts ? `(${drafts})` : ''}`;

	const showCompletedSessionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		dispatch(setShowCompleted(checked));
	};

	const showDraftsOnlyHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		dispatch(setShowDraftsOnly(checked));
	};

	const showBTSessionsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		dispatch(setIncludeBTEvents(checked));
	};

	const showBCBASessionsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		dispatch(setIncludeBCBAEvents(checked));
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<CustomCheckBox
				item={{
					checked: showDraftsOnly,
					id: '3',
					label: labelDrafts,
				}}
				onChange={showDraftsOnlyHandler}
			/>
			<CustomCheckBox
				item={{
					checked: showCompleted,
					id: '0',
					label: labelCompleted,
				}}
				onChange={showCompletedSessionHandler}
			/>
			{!therapist?.id && !client?.id && (
				<>
					{role?.section.id === AdminTypes.bcba && (
						<CustomCheckBox
							item={{
								checked: includeBTEvents,
								id: '1',
								label: labelBT,
							}}
							onChange={showBTSessionsHandler}
						/>
					)}
					{role?.section.id === AdminTypes.cs && (
						<CustomCheckBox
							item={{
								checked: includeBCBAEvents,
								id: '2',
								label: labelBCBA,
							}}
							onChange={showBCBASessionsHandler}
						/>
					)}
				</>
			)}
		</div>
	);
};

const Filters = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const role = useSelector((state) => state.account.role);
	const open = useSelector((state) => state.calendarHelper.popups.filters);

	const closeFilterHandler = () => dispatch(setOpenFilters(false));

	const canActionDone = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs;

	const displayDefaultViewHandler = useCallback(() => {
		dispatch(setClient(null));
		dispatch(setTherapist(null));
		dispatch(clearEvents());
		dispatch(setSearchTherapist(''));
		dispatch(setSearchClient(''));
		dispatch(setApplyFilter(false));
		dispatch(setShowCompleted(false));
		dispatch(setShowDraftsOnly(false));
		dispatch(setIncludeBTEvents(false));
		dispatch(setIncludeBCBAEvents(false));
	}, [dispatch]);

	const applyFiltersHandler = () => {
		dispatch(setApplyFilter(false));
		closeFilterHandler();
	};

	return (
		<CustomDrawer
			onClose={closeFilterHandler}
			open={open}
			label="Search options"
			MainButton={() => <CustomButton title="Search" className="flex100" onClick={applyFiltersHandler} />}
			HelperButton={() => <CustomButton title="Reset" buttonType="secondaryGray" onClick={displayDefaultViewHandler} />}
			Content={() => (
				<>
					{canActionDone && <AdminTools />}
					<CalendarFilters />
				</>
			)}
		/>
	);
};

export default Filters;
